import * as z from 'zod';

/**
 * One customer review as the reviews screen renders it (Figma 26295:98056).
 *
 * Narrower than the wire's `v1Review`: the screen shows an order number, a
 * relative timestamp, a star count and the comment. `restaurant_id` is dropped
 * — the merchant-scoped list spans every branch and the design does not name
 * one per row.
 */
export const reviewSchema = z.object({
  id: z.string(),
  /**
   * What «Заказ: …» prints. The SHORT `order_number` when the backend has one,
   * else the `order_id` UUID — see `toReview`.
   */
  orderLabel: z.string(),
  /** Raw creation timestamp, kept so the list can sort by date. */
  createdAt: z.string(),
  /** 0–5 stars. */
  rating: z.number().min(0).max(5),
  /** The comment body; empty for a rating left without one. */
  text: z.string(),
});
export type Review = z.infer<typeof reviewSchema>;

/**
 * How the list is ordered — the «По дате» pill (Figma 26295:98151).
 *
 * `default` is the server's own order, deliberately distinct from `newest`:
 * the endpoint has no sort parameter, so "default" means "whatever the API
 * returned" and is the only option that involves no client-side reordering.
 */
export const reviewSortSchema = z.enum(['default', 'newest', 'oldest']);
export type ReviewSort = z.infer<typeof reviewSortSchema>;

/**
 * How the list is filtered by score — the «По рейтингу» pill (Figma
 * 26295:98370). `high` is 4–5 stars, `low` is 1–2; the design offers no
 * band for 3, which lands in neither.
 */
export const reviewRatingFilterSchema = z.enum(['default', 'high', 'low']);
export type ReviewRatingFilter = z.infer<typeof reviewRatingFilterSchema>;
